/**
 * Os compromissos marcados: o que vem, o que aconteceu, e o que dá para desmarcar.
 *
 * **A conversa continua sendo o caminho.** Compromisso nasce falando — "marca o
 * dentista do Theo dia 12 às 9" — e a Ruth repete tudo antes de gravar. O
 * formulário daqui e o convite do rodapé são a saída de quando a Ruth não
 * entende, ou de quando a mãe está sem voz para falar.
 *
 * **E agora ela também diz o que houve.** Esta tela leva o nome do compromisso
 * e era a única das três que não marcava: a mãe dizia "fui" em Hoje e na
 * Semana, e justamente aqui, onde ela vem rever o que está marcado, não tinha
 * como. O mesmo objeto em três telas, e a que tem o nome dele calada.
 *
 * **A marca é memória dela, não boletim meu** — é a linha que segura a regra 2.
 * Dia sem marca não é dia falhado; é dia sem resposta, e continua sem resposta
 * para sempre, se ela quiser. Nada aqui diz "pendente", nada conta, nada compara
 * datas, nada fica vermelho por não ter sido tocado. Muda de aparência só o
 * cartão em que ela mesma tocou, e existe como desfazer o toque errado.
 *
 * **Não há mockup desta tela em docs/design.** O cartão vem do compromisso da
 * tela Hoje — hora à esquerda, ícone de calendário, o lugar embaixo — e o
 * agrupamento por dia vem da tela Semana. É desenho meu, precisa de revisão.
 *
 * Desmarcar pergunta antes, com os dois botões da conversa: é a única coisa
 * irreversível da tela, e um toque errado tiraria uma consulta da agenda.
 */

import { useCallback, useEffect, useState, type FormEvent } from 'react'

import {
  api,
  fraseDoErro,
  dataPorExtenso,
  hhmm,
  type CompromissoMarcado,
  type CompromissoNovo,
  type CorrecaoDeCompromisso,
} from '../api'
import { CorrecaoDoCompromisso } from '../componentes/CorrecaoDoCompromisso'
import '../estilos/conserto.css'
import { Icone } from '../componentes/Icone'
import '../estilos/compromissos.css'

type Props = {
  /** Falar continua sendo o caminho; o convite do rodapé leva à Ruth. */
  aoFalarComARuth: () => void
}

export function Compromissos({ aoFalarComARuth }: Props) {
  const [compromissos, setCompromissos] = useState<CompromissoMarcado[] | null>(null)
  const [hoje, setHoje] = useState<string | null>(null)
  const [perguntando, setPerguntando] = useState<string | null>(null)
  const [desmarcando, setDesmarcando] = useState<string | null>(null)
  const [corrigindo, setCorrigindo] = useState<string | null>(null)
  const [marcando, setMarcando] = useState(false)
  const [erro, setErro] = useState<string | null>(null)
  /**
   * Em que linha o "fui" está em curso, e em qual ele falhou.
   *
   * Pela chave, que junta o compromisso e a data: as duas coisas são de uma
   * data só, e guardá-las pelo id acenderia a quinta quando ela tocasse na
   * terça. O erro fica junto da chave porque ele é dito dentro do cartão — a
   * lista vai a sessenta dias, e uma frase no alto da tela é invisível para
   * quem tocou lá embaixo.
   */
  const [dizendo, setDizendo] = useState<string | null>(null)
  const [erroDaMarca, setErroDaMarca] = useState<{ chave: string; frase: string } | null>(null)

  const carregar = useCallback(async () => {
    try {
      const resposta = await api.compromissos()
      setCompromissos(resposta.compromissos)
      setHoje(resposta.hoje)
      setErro(null)
    } catch (e) {
      setErro(fraseDoErro(e, 'não consegui abrir seus compromissos'))
    }
  }, [])

  useEffect(() => {
    void carregar()
  }, [carregar])

  /**
   * A correção volta com o compromisso inteiro, e é ele que substitui a linha.
   *
   * Uma aula que se repete aparece em várias datas com o mesmo id: trocar
   * todas de uma vez é o certo — mudou a hora da aula, mudou em todas.
   */
  const salvar = useCallback(async (id: string, campos: CorrecaoDeCompromisso) => {
    const corrigido = await api.corrigirCompromisso(id, campos)
    setCompromissos((antes) =>
      (antes ?? []).map((c) =>
        c.id === id ? { ...corrigido, quando: c.quando } : c,
      ),
    )
    setCorrigindo(null)
    // Dia ou dias mudaram: as datas da lista já não são as certas.
    if (campos.quando || campos.repete_dias) void carregar()
  }, [carregar])

  const desmarcar = useCallback(async (id: string) => {
    setDesmarcando(id)
    setErro(null)
    try {
      await api.cancelarCompromisso(id)
      // A rota devolve só "ok": quem some com a linha é a tela.
      setCompromissos((antes) => (antes ?? []).filter((c) => c.id !== id))
      setPerguntando(null)
    } catch (e) {
      setErro(fraseDoErro(e, 'não consegui desmarcar isso agora'))
    } finally {
      setDesmarcando(null)
    }
  }, [])

  /**
   * "Fui", e o tirar, que é metade de poder marcar.
   *
   * **Só a linha daquela data muda** — é o contrário do `salvar` aqui de cima,
   * e de propósito: corrigir a hora da aula corrige todas as terças e quintas,
   * mas ter ido na terça não diz nada sobre a quinta.
   *
   * Por isso a resposta do servidor não vira linha. Ela volta com o começo da
   * série, não com o dia que eu mandei: escrevê-la por cima moveria a aula da
   * quinta para a data de origem, e a marca cairia no dia errado. Quem muda a
   * linha aqui é a tela, e só a linha tocada.
   *
   * Não é otimista. O cartão só troca depois de o servidor confirmar: mostrar
   * a marca e não ter gravado é o erro que a mãe não tem como perceber.
   */
  const dizer = useCallback(async (compromisso: CompromissoMarcado) => {
    const chave = chaveDa(compromisso)
    const fui = !compromisso.cumprido

    setDizendo(chave)
    setErroDaMarca(null)
    try {
      await (fui
        ? api.compromissoCumprido(compromisso.id, compromisso.quando)
        : api.compromissoDescumprido(compromisso.id, compromisso.quando))
      setCompromissos((antes) =>
        (antes ?? []).map((c) => (chaveDa(c) === chave ? { ...c, cumprido: fui } : c)),
      )
    } catch (e) {
      setErroDaMarca({
        chave,
        frase: fraseDoErro(
          e,
          fui ? 'Não consegui anotar agora. Tenta de novo?' : 'Não consegui tirar a marca agora.',
        ),
      })
    } finally {
      setDizendo(null)
    }
  }, [])

  const marcar = useCallback(
    async (novo: CompromissoNovo) => {
      await api.marcarCompromisso(novo)
      setMarcando(false)
      await carregar()
    },
    [carregar],
  )

  const dias = agrupar(compromissos ?? [])
  const amanha = hoje ? diaSeguinte(hoje) : null

  return (
    <>
      <header className="cabeca">
        <div className="cabeca__datas">
          <h1 className="cabeca__titulo">Compromissos</h1>
          <span className="cabeca__data">o que vem pela frente</span>
        </div>
      </header>

      <div className="corpo">
        {erro && <p className="vazio">{erro}</p>}

        {compromissos && dias.length === 0 && !erro && (
          <p className="vazio">Nada marcado por enquanto.</p>
        )}

        {dias.map((dia) => (
          <div className="agenda__dia" key={dia.quando}>
            <span className={`agenda__data ${dia.quando === hoje ? 'agenda__data--hoje' : ''}`}>
              {separador(dia.quando, hoje, amanha)}
            </span>

            {dia.itens.map((compromisso) => (
              <Marcado
                key={chaveDa(compromisso)}
                compromisso={compromisso}
                jaFoi={hoje !== null && compromisso.quando <= hoje}
                perguntando={perguntando === chaveDa(compromisso)}
                desmarcando={desmarcando === compromisso.id}
                corrigindo={corrigindo === chaveDa(compromisso)}
                dizendo={dizendo === chaveDa(compromisso)}
                erroDaMarca={erroDaMarca?.chave === chaveDa(compromisso) ? erroDaMarca.frase : null}
                aoDizer={() => void dizer(compromisso)}
                aoPerguntar={() => setPerguntando(chaveDa(compromisso))}
                aoDeixar={() => setPerguntando(null)}
                aoDesmarcar={() => void desmarcar(compromisso.id)}
                aoCorrigir={() => setCorrigindo(chaveDa(compromisso))}
                aoFechar={() => setCorrigindo(null)}
                aoSalvar={(campos) => salvar(compromisso.id, campos)}
              />
            ))}
          </div>
        ))}

        {marcando ? (
          <Marcar
            aoMarcar={marcar}
            aoFechar={() => setMarcando(false)}
            hoje={hoje}
          />
        ) : (
          <button type="button" className="convite" onClick={() => setMarcando(true)}>
            <Icone nome="somar" tamanho={20} />
            Marcar uma coisa
          </button>
        )}

        <button type="button" className="convite" onClick={aoFalarComARuth}>
          <Icone nome="somar" tamanho={20} />
          Fala pra Ruth o que você quer marcar
        </button>
      </div>
    </>
  )
}

/**
 * Um cartão da agenda: o que está marcado, e o que ela diz que houve.
 *
 * **A marca aqui é escrita, não é bolinha.** Em Hoje ela é a bolinha porque a
 * tarefa da linha de cima já é bolinha, e a mãe não deve aprender dois jeitos
 * de dizer "fiz" na mesma lista. Nesta tela não existe tarefa nenhuma: uma
 * bolinha solta seria um símbolo sem quem o ensine, num cartão em que todo o
 * resto — "Corrigir", "Desmarcar" — diz com palavra o que faz. Então vale a
 * palavra da Semana, que resolveu o mesmo problema: "Fui" e "Tirar a marca",
 * as mesmas duas frases, para não existir um terceiro jeito.
 *
 * Ela vai embaixo, com um traço separando: três botões escritos numa linha só
 * não cabem em 375px sem espremer o título, que é o que a mãe veio ler.
 *
 * **O que ela marcou troca de ícone, e não risca.** O risco de Hoje quer dizer
 * "resolvido, segue" numa lista de um dia; aqui a lista vai a sessenta dias, e
 * numa coluna comprida ele passaria a dizer que o cartão sem risco é o que
 * falta — boletim, que é o que a regra 2 proíbe. Pior nesta tela do que em
 * qualquer outra: o título riscado fica a um dedo de um botão escrito
 * "Desmarcar", e risco é o desenho universal de "cancelado". A mãe leria que a
 * consulta caiu. O tique no lugar do calendário diz a mesma coisa sem mentir, e
 * deixa em paz o título que ela está procurando.
 */
function Marcado({
  compromisso,
  jaFoi,
  perguntando,
  desmarcando,
  corrigindo,
  dizendo,
  erroDaMarca,
  aoDizer,
  aoPerguntar,
  aoDeixar,
  aoDesmarcar,
  aoCorrigir,
  aoFechar,
  aoSalvar,
}: {
  compromisso: CompromissoMarcado
  /** O dia já chegou. Não se vai a uma coisa que ainda não aconteceu. */
  jaFoi: boolean
  perguntando: boolean
  desmarcando: boolean
  corrigindo: boolean
  dizendo: boolean
  erroDaMarca: string | null
  aoDizer: () => void
  aoPerguntar: () => void
  aoDeixar: () => void
  aoDesmarcar: () => void
  aoCorrigir: () => void
  aoFechar: () => void
  aoSalvar: (campos: CorrecaoDeCompromisso) => Promise<void>
}) {
  const onde = ondeEComQuem(compromisso)
  const aviso = quemLembra(compromisso)

  return (
    <div className="agenda__linha">
      <span className="agenda__hora hora">{hhmm(compromisso.hora)}</span>

      <div className="agenda__cartao">
        <div className="agenda__topo">
          <span className={`agenda__icone ${compromisso.cumprido ? 'agenda__icone--feito' : ''}`}>
            <Icone nome={compromisso.cumprido ? 'check' : 'calendario'} tamanho={20} />
          </span>

          <span className="agenda__texto">
            <span className="agenda__titulo">{compromisso.titulo}</span>
            {onde && <span className="agenda__meta">{onde}</span>}
            {aviso && <span className="agenda__aviso">{aviso}</span>}
          </span>

          {!perguntando && !corrigindo && (
            <>
              <button
                type="button"
                className="agenda__corrigir"
                aria-label={`Corrigir ${compromisso.titulo}`}
                onClick={aoCorrigir}
              >
                Corrigir
              </button>
              <button
                type="button"
                className="agenda__desmarcar"
                aria-label={`Desmarcar ${compromisso.titulo}`}
                onClick={aoPerguntar}
              >
                Desmarcar
              </button>
            </>
          )}
        </div>

        {/* Some junto com "Corrigir" e "Desmarcar": uma decisão por cartão de
            cada vez. Com o formulário aberto, ou com a pergunta do desmarcar na
            tela, mais um botão embaixo é a mãe sem saber no que está mexendo. */}
        {/* "Fui" só aparece de hoje para trás. Numa lista que vai a sessenta
            dias, sessenta botões "Fui" empilhados debaixo de datas futuras são
            uma pergunta sem resposta possível — e a silhueta de um checklist
            esperando ser preenchido, que é a forma do boletim mesmo sem
            nenhuma palavra que meça (regra 2).

            "Tirar a marca" continua aparecendo em qualquer dia: se ela tocou
            sem querer, o desfazer não pode ficar preso. */}
        {!perguntando && !corrigindo && (jaFoi || compromisso.cumprido) && (
          <div className="agenda__marca">
            {compromisso.cumprido && <span className="agenda__marcado">Você marcou que foi.</span>}
            <button
              type="button"
              className={compromisso.cumprido ? 'conf-pessoa__deixar' : 'agenda__fui'}
              // O rótulo leva o título porque a tela empilha dezenas de cartões:
              // "Fui" repetido trinta vezes não diz a ninguém qual é qual.
              aria-label={
                compromisso.cumprido
                  ? `tirar a marca de ${compromisso.titulo}`
                  : `marcar que fui, ${compromisso.titulo}`
              }
              disabled={dizendo}
              onClick={aoDizer}
            >
              {compromisso.cumprido ? 'Tirar a marca' : 'Fui'}
            </button>
          </div>
        )}

        {/* Dentro do cartão, onde o dedo dela estava. No alto da tela, quem
            tocou no compromisso de daqui a um mês nunca veria o recado. */}
        {erroDaMarca && (
          <p className="agenda__erro" role="alert">
            {erroDaMarca}
          </p>
        )}

        {corrigindo && (
          <CorrecaoDoCompromisso
            compromisso={compromisso}
            aoSalvar={aoSalvar}
            aoFechar={aoFechar}
          />
        )}

        {perguntando && (
          <div className="agenda__confirma">
            <span className="agenda__pergunta">Desmarco mesmo?</span>
            <button
              type="button"
              className="confirmar__sim"
              disabled={desmarcando}
              onClick={aoDesmarcar}
            >
              Desmarca
            </button>
            <button type="button" className="confirmar__nao" onClick={aoDeixar}>
              Deixa
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

const DIAS_DO_MARCAR = [
  { codigo: 'seg', curto: 'seg' },
  { codigo: 'ter', curto: 'ter' },
  { codigo: 'qua', curto: 'qua' },
  { codigo: 'qui', curto: 'qui' },
  { codigo: 'sex', curto: 'sex' },
  { codigo: 'sab', curto: 'sáb' },
  { codigo: 'dom', curto: 'dom' },
]

/**
 * Marcar um compromisso sem falar com a Ruth.
 *
 * Falar continua sendo o caminho — "pediatra da Ana quinta às 15h30" é mais
 * rápido do que qualquer formulário. Isto é a saída de quando ela não está com
 * voz, quando a Ruth não entendeu, ou quando é mais fácil tocar do que
 * explicar. Até agora essa saída não existia: a tela listava, corrigia e
 * desmarcava, e **pôr** uma coisa nova só pela conversa.
 */
function Marcar({
  aoMarcar,
  aoFechar,
  hoje,
}: {
  aoMarcar: (novo: CompromissoNovo) => Promise<void>
  aoFechar: () => void
  hoje: string | null
}) {
  const [titulo, setTitulo] = useState('')
  const [quando, setQuando] = useState(hoje ?? '')
  const [hora, setHora] = useState('09:00')
  const [local, setLocal] = useState('')
  const [dias, setDias] = useState<string[]>([])
  const [salvando, setSalvando] = useState(false)
  const [erro, setErro] = useState<string | null>(null)

  const salvar = async (evento: FormEvent) => {
    evento.preventDefault()
    if (salvando || !titulo.trim() || !quando) return

    setSalvando(true)
    setErro(null)
    try {
      await aoMarcar({
        titulo: titulo.trim(),
        quando,
        hora: `${hora}:00`,
        local: local.trim() || null,
        repete_dias: dias,
      })
    } catch (e) {
      setErro(fraseDoErro(e, 'Não consegui marcar agora.'))
      setSalvando(false)
    }
  }

  return (
    <form className="conf-pessoa" onSubmit={salvar}>
      <label className="conf-pessoa__campo">
        <span>O que é</span>
        <input
          value={titulo}
          onChange={(e) => setTitulo(e.target.value)}
          placeholder="Pediatra da Ana"
          maxLength={160}
          autoFocus
        />
      </label>

      <div className="conf-pessoa__idade">
        <label className="conf-pessoa__campo">
          <span>Que dia</span>
          <input type="date" value={quando} onChange={(e) => setQuando(e.target.value)} />
        </label>
        <label className="conf-pessoa__campo">
          <span>Que hora</span>
          <input type="time" value={hora} onChange={(e) => setHora(e.target.value)} />
        </label>
      </div>

      <label className="conf-pessoa__campo">
        <span>Onde</span>
        <input
          value={local}
          onChange={(e) => setLocal(e.target.value)}
          placeholder="deixe vazio se não tem lugar"
          maxLength={200}
        />
      </label>

      <fieldset className="agenda__dias">
        <legend>Se repete? Marque os dias</legend>
        {DIAS_DO_MARCAR.map(({ codigo, curto }) => (
          <label key={codigo} className="agenda__dia-escolha">
            <input
              type="checkbox"
              checked={dias.includes(codigo)}
              onChange={(e) =>
                setDias((antes) =>
                  e.target.checked ? [...antes, codigo] : antes.filter((d) => d !== codigo),
                )
              }
            />
            {curto}
          </label>
        ))}
      </fieldset>

      {erro && (
        <p className="agenda__erro" role="alert">
          {erro}
        </p>
      )}

      <div className="conf-pessoa__acoes">
        <button
          type="submit"
          className="botao-primario"
          disabled={salvando || !titulo.trim() || !quando}
        >
          {salvando ? 'Marcando…' : 'Marcar'}
        </button>
        <button type="button" className="conf-pessoa__deixar" onClick={aoFechar}>
          Deixa pra lá
        </button>
      </div>
    </form>
  )
}

/**
 * "Theo · Asa Sul · até 16h30" — só o que é verdade, e nada repetido.
 *
 * O nome sai da linha quando já está no título: "Pediatra da Ana · Ana" não
 * acrescenta nada, e a Ruth não fala duas vezes a mesma coisa.
 */
function ondeEComQuem(compromisso: CompromissoMarcado): string {
  const { para_quem, titulo, local, fim, repete } = compromisso
  const nome =
    para_quem && !titulo.toLowerCase().includes(para_quem.toLowerCase()) ? para_quem : null

  // `repete` entra porque a aula aparece em várias datas: sem ele a mãe vê a
  // mesma linha quatro vezes na lista e não sabe se marcou quatro vezes.
  return [nome, local, fim ? `até ${hhmm(fim)}` : null, repete || null]
    .filter(Boolean)
    .join(' · ')
}

/**
 * Quem lembra é a Ruth, e a mãe precisa ler isso para poder esquecer.
 *
 * É informação, não botão: mudar o aviso é conversa, como marcar.
 */
function quemLembra(compromisso: CompromissoMarcado): string | null {
  const { lembrete_vespera, lembrete_dia } = compromisso
  if (lembrete_vespera && lembrete_dia) return 'a Ruth avisa na véspera e no dia'
  if (lembrete_vespera) return 'a Ruth avisa na véspera'
  if (lembrete_dia) return 'a Ruth avisa no dia'
  return null
}

/**
 * Que linha é esta: o compromisso **e** a data.
 *
 * A aula que se repete é uma linha só no banco e vários cartões na tela, todos
 * com o mesmo id. A marca é de uma data só, então é a data que separa um cartão
 * do outro — sem ela, dizer "fui" na terça acenderia também a quinta.
 */
function chaveDa(compromisso: CompromissoMarcado): string {
  return `${compromisso.id}-${compromisso.quando}`
}

type DiaDaAgenda = { quando: string; itens: CompromissoMarcado[] }

/** A lista já vem em ordem de hora: basta cortar onde o dia vira. */
function agrupar(compromissos: CompromissoMarcado[]): DiaDaAgenda[] {
  const dias: DiaDaAgenda[] = []

  for (const compromisso of compromissos) {
    const ultimo = dias[dias.length - 1]
    if (ultimo && ultimo.quando === compromisso.quando) ultimo.itens.push(compromisso)
    else dias.push({ quando: compromisso.quando, itens: [compromisso] })
  }
  return dias
}

/** O "hoje" vem do servidor, no fuso da casa — nunca do relógio do aparelho. */
function separador(quando: string, hoje: string | null, amanha: string | null): string {
  if (quando === hoje) return 'Hoje'
  if (quando === amanha) return 'Amanhã'
  return dataPorExtenso(quando)
}

/** O dia seguinte pelo `Date` local, que sabe virar mês e ano sozinho. */
function diaSeguinte(dia: string): string {
  const [ano, mes, numero] = dia.split('-').map(Number)
  const seguinte = new Date(ano, mes - 1, numero + 1)
  return iso(seguinte.getFullYear(), seguinte.getMonth() + 1, seguinte.getDate())
}

function iso(ano: number, mes: number, dia: number): string {
  return `${ano}-${String(mes).padStart(2, '0')}-${String(dia).padStart(2, '0')}`
}
